import type { TableProps } from 'antd';
import { ConfigProvider, Table as AntTable, Dropdown } from 'antd';
import colors from '../../constants/colors';
import { Colors } from 'basic-ui-lib/constants';
import { Ic3Dots } from 'basic-ui-lib/icons';
import { useMemo } from 'react';

export interface ActionProps<T> {
  label: string | ((record: T) => string);
  icon: React.ReactNode | ((record: T) => React.ReactNode);
  onClick: (record: T) => void;
  hidden?: (record: T) => boolean;
}

interface TableExtendedProps<T> extends TableProps<T> {
  actions?: ActionProps<T>[];
}

const Table = <T extends Record<string, any>>(props: TableExtendedProps<T>) => {
  const { actions = [], columns = [], ...restProps } = props;

  const getRowClassName = (record: T, index: number) => {
    let additionalClassName = '';

    if (props.rowClassName) {
      if (typeof props.rowClassName === 'function') {
        additionalClassName = props.rowClassName(record, index, 0);
      } else {
        additionalClassName = props.rowClassName;
      }
    }

    return `fzt-table ${additionalClassName}`;
  };

  const actionColumn = useMemo(() => {
    if (actions.length === 0) {
      return [];
    }

    return [
      {
        title: 'ACTIONS',
        key: '__actions__',
        width: 120,
        render: (_: any, record: T) => <ActionsDropdown record={record} actions={actions} />,
      },
    ];
  }, [actions]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: Colors.neutral.bg[3],
            headerColor: Colors.neutral.text[6],
            rowHoverBg: colors.neutral.bg[2],
            borderColor: Colors.neutral.border[1],
            headerBorderRadius: 8,
            headerSortActiveBg: '#3D4D65',
            headerSortHoverBg: '#3D4D65',
            bodySortBg: colors.primary[2],
            cellPaddingBlock: 12,
            cellPaddingInline: 16,
            fontWeightStrong: 500,
          },
        },
      }}
    >
      <AntTable<T>
        {...restProps}
        className='fzt-table'
        rowClassName={getRowClassName}
        columns={[...columns, ...actionColumn]}
      />
    </ConfigProvider>
  );
};

export default Table;

const ActionsDropdown = <T extends Record<string, any>>({
  record,
  actions,
  hidden,
}: {
  record: T;
  actions: ActionProps<T>[];
  hidden?: boolean;
}) => {
  const visibleActions = actions.filter(
    (a) => !a.hidden?.(record)
  );

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: visibleActions.map((a, i) => ({
          key: i,
          label: typeof a.label === 'function' ? a.label(record) : a.label,
          icon: typeof a.icon === 'function' ? a.icon(record) : a.icon,
          onClick: () => a.onClick?.(record),
        })),
        className:'fzt-dropdown-menu',
        hidden,
      }}
    >
      <button type='button'>
        <Ic3Dots />
      </button>
    </Dropdown>
  );
};
